import { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import { textContrastIsAA } from 'helpers/getContrast'

import { BellIcon } from '@pleio/icons/bell'
import { BellFillIcon } from '@pleio/icons/bell-fill'

import Popover from 'js/components/Popover'
import NavigationAction from 'js/layout/Navigation/components/NavigationAction'
import NavigationBadge from 'js/layout/Navigation/components/NavigationBadge'
import { globalStateContext } from 'js/lib/GlobalState'

import NavigationNotificationsView from './NavigationNotificationsView'

const NavigationNotifications = ({ viewer }) => {
    const { globalState } = useContext(globalStateContext)
    const popoverActionsRef = useRef(null)

    const [visible, setVisible] = useState(false)

    const { data, startPolling, stopPolling } = useQuery(
        NOTIFICATIONS_UNREAD_QUERY,
        {
            fetchPolicy: 'cache-and-network',
        },
    )

    useEffect(() => {
        if (globalState.windowIsActive && globalState.userIsActive) {
            startPolling(120000) // every 120 seconds
        } else {
            stopPolling()
        }
    }, [
        globalState.windowIsActive,
        globalState.userIsActive,
        startPolling,
        stopPolling,
    ])

    const handleHideNotifications = () => {
        const popoverActions = popoverActionsRef.current
        if (popoverActions) {
            popoverActions.close()
        }
    }

    const hasUnread =
        data && data.notifications && data.notifications.totalUnread !== 0

    const { t } = useTranslation()

    if (viewer && !viewer.user) {
        return null
    }

    return (
        <span>
            <Popover
                actionsRef={popoverActionsRef}
                open={visible}
                setOpen={setVisible}
                content={
                    <NavigationNotificationsView
                        viewer={viewer}
                        visible={visible}
                        hasUnread={hasUnread}
                        hideNotifications={handleHideNotifications}
                    />
                }
                sideOffset={0}
                positionIsFixed={true}
                ariaLabel={t('notifications.title')}
            >
                <NavigationAction
                    tooltip={t('notifications.title')}
                    aria-label={
                        data
                            ? t('notifications.show-new-notifications', {
                                  count: data.notifications.totalUnread,
                              })
                            : null
                    }
                >
                    {textContrastIsAA(
                        getComputedStyle(document.documentElement)
                            .getPropertyValue('--ref-palette-header-main')
                            .trim(),
                    ) ? (
                        <BellFillIcon />
                    ) : (
                        <BellIcon />
                    )}
                    {hasUnread && (
                        <NavigationBadge>
                            {data && data.notifications.totalUnread < 100
                                ? data.notifications.totalUnread
                                : '99+'}
                        </NavigationBadge>
                    )}
                </NavigationAction>
            </Popover>
        </span>
    )
}

export const NOTIFICATIONS_UNREAD_QUERY = gql`
    query NotificationsUnread {
        notifications {
            totalUnread
        }
    }
`

export default NavigationNotifications
